'use client';

import { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { Input } from '@ps/ui/atoms/input';
import { Textarea } from '@ps/ui/atoms/textarea';
import { cn } from '@ps/ui/utils';
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';

import {
  CHANNEL_DESCRIPTION_MAX_LENGTH,
  CHANNEL_NAME_MAX_LENGTH,
} from '../../functions/channel/channelFieldLimits';
import { ChannelVisibility } from '../../types/ChannelVisibility';
import { ChannelBroadcastField } from './ChannelBroadcastField';
import { ChannelIconUploadRow } from './ChannelIconUploadRow';
import { ChannelSubscriptionLevelField } from './ChannelSubscriptionLevelField';

/**
 * The one form shape shared by the Create and Edit channel modals. Mirrors
 * PopshopRN's `CommunityChannelInfo` validation config: name / description /
 * icon / visibility / minimumSubscriptionLevel are required, broadcast is
 * optional. `visibility` and `minimumSubscriptionLevel` always carry a default,
 * so their required-ness never fails in practice — they're marked required for
 * parity, not because a user can clear them.
 */
export interface ChannelFormValues {
  name: string;
  description: string;
  icon?: string;
  visibility: ChannelVisibility;
  isBroadcastChannel: boolean;
  minimumSubscriptionLevel: PopStoreExtendedSubscriptionLevel;
}

const kLabel = 'mb-1 block text-sm font-medium text-content-1';

function RequiredMark() {
  return <span className="text-red-400">*</span>;
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return (
    <p role="alert" className="mt-1 text-xs text-red-400">
      {message}
    </p>
  );
}

/**
 * All create/edit channel form fields, wired to the enclosing react-hook-form
 * (via `FormProvider`). Required fields carry a `*` marker and validate on
 * submit, so the modal shows a per-field message instead of letting the backend
 * reject with an opaque error. `idPrefix` keeps input ids unique per modal.
 */
export function ChannelFormFields({
  idPrefix,
  disabled,
}: {
  idPrefix: string;
  disabled?: boolean;
}) {
  const { control, register } = useFormContext<ChannelFormValues>();
  // `useFormState` (not `formState` off the context) subscribes THIS component to
  // error changes. The `formState` proxy is owned by the modal that called
  // `useForm`; since the modal never reads `errors`, reading them only here
  // wouldn't re-render on submit — validation messages would never appear.
  const { errors } = useFormState({ control });
  const visibility = useWatch({ control, name: 'visibility' });

  return (
    <>
      <div>
        <span className={kLabel}>
          Channel Icon
          <RequiredMark />
        </span>
        <Controller
          control={control}
          name="icon"
          rules={{ required: 'Channel icon is required.' }}
          render={({ field }) => (
            <ChannelIconUploadRow
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
            />
          )}
        />
        <FieldError message={errors.icon?.message} />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-name`} className={kLabel}>
          Channel Name
          <RequiredMark />
        </label>
        <Input
          id={`${idPrefix}-name`}
          {...register('name', {
            required: 'Channel name is required.',
            validate: value =>
              value.trim().length > 0 || 'Channel name is required.',
          })}
          maxLength={CHANNEL_NAME_MAX_LENGTH}
          placeholder="Enter channel name…"
          className="text-content-1 bg-surface-2 placeholder:text-content-4 border-transparent"
          autoFocus
        />
        <FieldError message={errors.name?.message} />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-desc`} className={kLabel}>
          Channel Description
          <RequiredMark />
        </label>
        <Textarea
          id={`${idPrefix}-desc`}
          {...register('description', {
            required: 'Channel description is required.',
            validate: value =>
              value.trim().length > 0 || 'Channel description is required.',
          })}
          maxLength={CHANNEL_DESCRIPTION_MAX_LENGTH}
          placeholder="Enter channel description…"
          className="text-content-1 bg-surface-2 placeholder:text-content-4 border-transparent"
        />
        <FieldError message={errors.description?.message} />
      </div>

      <Controller
        control={control}
        name="visibility"
        render={({ field }) => (
          <fieldset>
            <legend className="text-content-1 mb-2 text-sm font-medium">
              Channel Visibility
              <RequiredMark />
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <VisibilityOption
                title="Public"
                description="Everyone can see channel messages."
                selected={field.value === ChannelVisibility.Public}
                onClick={() => field.onChange(ChannelVisibility.Public)}
              />
              <VisibilityOption
                title="Private"
                description="Only visible to channel members."
                selected={field.value === ChannelVisibility.Private}
                onClick={() => field.onChange(ChannelVisibility.Private)}
              />
            </div>
          </fieldset>
        )}
      />

      <Controller
        control={control}
        name="isBroadcastChannel"
        render={({ field }) => (
          <ChannelBroadcastField
            value={field.value}
            onChange={field.onChange}
            disabled={disabled}
          />
        )}
      />

      <Controller
        control={control}
        name="minimumSubscriptionLevel"
        render={({ field }) => (
          <ChannelSubscriptionLevelField
            value={field.value}
            onChange={field.onChange}
            visibility={visibility}
            disabled={disabled}
          />
        )}
      />
    </>
  );
}

function VisibilityOption({
  title,
  description,
  selected,
  onClick,
}: {
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-md border px-3 py-2 text-left transition',
        selected
          ? 'border-brand bg-brand text-content-on-brand'
          : 'text-content-1 border-line-strong bg-surface-1 hover:border-raise/20',
      )}
    >
      <p className="text-sm font-semibold">{title}</p>
      <p
        className={cn(
          'mt-1 text-xs',
          // `sink` is the inverse of `raise`, i.e. BLACK in dark but WHITE in
          // light — so on this mint fill the heading read near-black at 10.1:1
          // while its own subtext washed out at 1.61:1 (QA). Dark hid it: there
          // the two agree by accident. The `theme-light:` override (6.7:1) is
          // the pattern every other `text-sink`-on-`brand` site already carries
          // — `AiCustomizationDock`, `SelectedTemplatePreview`,
          // `TransactionActivitiesFilterPanel`, `message/unsupported` — this
          // one was simply missing it, so dark stays exactly as it was.
          selected
            ? 'text-sink/80 theme-light:text-content-on-brand/80'
            : 'text-content-2',
        )}
      >
        {description}
      </p>
    </button>
  );
}
